import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, GitCompareArrows } from "lucide-react";

const scoreColor = (v) => (v >= 80 ? "#10B981" : v >= 65 ? "#84CC16" : v >= 50 ? "#F59E0B" : v >= 35 ? "#FB923C" : "#F43F5E");

const SCORE_LABELS = { business_quality: "Business Quality", financial_health: "Financial Health", growth: "Growth", valuation: "Valuation", risk: "Risk" };

// Every signal here is derived directly from the real scores already
// returned by /companies/:ticker — nothing is invented. Only scores at or
// above 65/100 are called out as a genuine strength.
export const strongestSignals = (scores) => {
  if (!scores) return [];
  return Object.entries(SCORE_LABELS)
    .filter(([key]) => key !== "risk")
    .map(([key, label]) => ({ key, label, value: scores[key] }))
    .filter((e) => e.value != null)
    .sort((a, b) => b.value - a.value);
};

export const whyItStandsOut = (scores) => {
  const strong = strongestSignals(scores).filter((s) => s.value >= 65);
  if (strong.length === 0) return null;
  if (strong.length === 1) return `A high ${strong[0].label} profile (${strong[0].value}/100) among LONGVIEW-covered companies.`;
  const [a, b] = strong;
  return `Strong ${a.label} and ${b.label} relative to other companies currently covered by LONGVIEW (${a.value} and ${b.value} respectively).`;
};

const SignalChip = ({ label, value }) => (
  <span
    className="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wide"
    style={{ borderColor: `${scoreColor(value)}40`, color: scoreColor(value), background: `${scoreColor(value)}14` }}
  >
    {label} {value}
  </span>
);

export const DiscoverCard = ({ c, primaryMetric = "business_quality" }) => {
  const signals = strongestSignals(c.scores).slice(0, 2);
  const reason = whyItStandsOut(c.scores);
  const primary = c.scores?.[primaryMetric];
  const primaryLabel = SCORE_LABELS[primaryMetric] || "LONGVIEW";

  return (
    <div data-testid={`discover-card-${c.ticker.toLowerCase()}`} className="lv-panel lv-panel-hover flex h-full flex-col p-4 transition-transform duration-200 hover:-translate-y-0.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs font-bold text-white">{c.ticker}</span>
            {c.cap_label && <span className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">{c.cap_label}</span>}
          </div>
          <h3 className="mt-1 truncate text-sm font-semibold text-white">{c.name}</h3>
          {c.sector && <p className="truncate text-[11px] text-zinc-500">{c.sector}</p>}
        </div>
        {primary != null && (
          <div className="shrink-0 text-right">
            <div className="font-mono text-2xl font-bold tabular-nums leading-none" style={{ color: scoreColor(primaryMetric === "risk" ? 100 - primary : primary) }}>{primary}</div>
            <div className="mt-1.5 h-[3px] w-10 rounded-full" style={{ background: `linear-gradient(90deg, transparent, ${scoreColor(primaryMetric === "risk" ? 100 - primary : primary)})` }} />
            <div className="mt-1 font-mono text-[8px] uppercase tracking-wider text-zinc-600">{primaryLabel}</div>
          </div>
        )}
      </div>

      {signals.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {signals.map((s) => <SignalChip key={s.key} label={s.label} value={s.value} />)}
        </div>
      )}

      <p className="mt-3 flex-1 border-t border-white/[0.06] pt-3 text-xs leading-relaxed text-zinc-400">
        {reason ? (<><span className="font-mono text-[9px] uppercase tracking-wider text-amber-400/80">Why it stands out · </span>{reason}</>) : c.discover_reason}
      </p>

      <div className="mt-3 flex items-center gap-2">
        <Link to={`/company/${c.ticker}`} data-testid={`discover-card-view-${c.ticker.toLowerCase()}`} className="lv-press flex flex-1 items-center justify-center gap-1 rounded-md border border-white/[0.1] py-1.5 font-mono text-[11px] text-zinc-300 transition-colors hover:border-amber-500/40 hover:text-amber-200">
          View Intelligence <ArrowUpRight className="h-3 w-3" />
        </Link>
        <Link to={`/compare?a=${c.ticker}`} data-testid={`discover-card-compare-${c.ticker.toLowerCase()}`} className="lv-press flex flex-1 items-center justify-center gap-1 rounded-md border border-white/[0.1] py-1.5 font-mono text-[11px] text-zinc-300 transition-colors hover:border-amber-500/40 hover:text-amber-200">
          Compare <GitCompareArrows className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
};
